import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { goTo } from '@adornis/router/client/open-href.js';
import type { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { CampusEvent } from '@chancenwerk/campus-learning-content/entities/CampusEvent.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { CampusSubject } from '@chancenwerk/db/enums/CampusSubject.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-textarea.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { filter, takeUntil } from 'rxjs';
import { CampusRoutes } from '../routes.js';

/**
 * @customElement campus-page-administration-event-edit
 */
@customElement('campus-page-administration-event-edit')
export class CampusPageAdministrationEventEdit extends CrPage {
  @property() public eventID = new RXController<Maybe<string>>(this, null);
  @property({ attribute: false }) public area = new RXController(this, '');
  @property({ attribute: false }) public editArea = false;

  @state() public event = new RXController(this, new CampusEvent({}));

  @state() private readonly _errorMsg = new RXController(this, '');

  private readonly _form = new FormController(this, this.event.observable, {
    success: campusEvt => this._handleFormSuccess(campusEvt),
    error: err => this._handleFormError(err),
  });

  private async _handleFormSuccess(campusEvent: CampusEvent) {
    try {
      let id: string | null = this.event.value._id;
      if (!id) {
        id = await campusEvent.create();
      } else {
        await campusEvent.save();
      }
      void ChromiumSnackbar.show('Event gespeichert');
      goTo(CampusRoutes.eventAdmin);
    } catch (error: any) {
      this._errorMsg.next(error.message);
    }
  }

  private _handleFormError(err: ValidationError<any>) {
    this._errorMsg.next(err.message);
  }

  constructor() {
    super();

    this.label = 'Event erstellen';
    this.backRoute = CampusRoutes.eventAdmin;
    this.permission = ChancenPermission.campus.EDIT_EVENTS;
  }

  override connectedCallback() {
    super.connectedCallback();

    this.eventID.observable.pipe(takeUntil(this.disconnected)).subscribe(id => {
      if (!id) {
        return;
      }

      CampusEvent.getByID<CampusEvent>(id)(CampusEvent.allFields)
        .then(event => {
          if (!event) {
            return;
          }
          this.event.next(event);
        })
        .catch(console.error);
    });

    this.event.observable
      .pipe(takeUntil(this.disconnected))
      .subscribe(e => (this.label = !e._id ? 'Event erstellen' : 'Event bearbeiten'));

    this.area.observable
      .pipe(takeUntil(this.disconnected), filter(Boolean))
      .subscribe(area => (this.event.value.area = area));
  }

  public override content() {
    return html`
      <cr-card space="md" margin="xs">
        ${this.editArea
          ? html`
              <cr-dropdown-selection
                .selectables=${[CampusArea.EMPLOYEES, CampusArea.STUDENTS]}
                placeholder="Personengruppe"
                @value-picked=${(e: ValueEvent<string>) => {
                  this.area.next(e.detail.value);
                }}
                .value=${this.area.value}
              ></cr-dropdown-selection>
            `
          : nothing}
        <cr-input ${this._form.field('title')} placeholder="Titel"></cr-input>
        ${this.area.value === CampusArea.STUDENTS
          ? html`
              <cr-dropdown-multiselection
                ${this._form.field('forGrades')}
                placeholder="Jahrgangsstufe"
                .selectables=${ChancenwerkUser.availableGrades}
              ></cr-dropdown-multiselection>
              <cr-dropdown-multiselection
                ${this._form.field('subjects')}
                placeholder="Schulfach"
                .selectables=${Object.values(CampusSubject)}
              ></cr-dropdown-multiselection>
            `
          : nothing}
        <cr-grid columns="1fr 1fr" space="md">
          <cr-date-picker-dropdown
            ${this._form.field('startDate')}
            placeholder="Anfang"
            click-to-save
            default-date=${DateTime.now()}
            @value-picked=${(e: ValueEvent<DateTime>) => {
              if (this.event.value.endDate && this.event.value.endDate.startOf('day') < e.detail.value.startOf('day')) {
                this.event.value.endDate = e.detail.value;
                this.event.next(this.event.value);
              }
            }}
          ></cr-date-picker-dropdown>
          <cr-date-picker-dropdown
            ${this._form.field('endDate')}
            placeholder="Ende"
            click-to-save
            .isDateBlocked=${(date: DateTime) =>
              this.event.value.startDate && date < this.event.value.startDate.startOf('day')}
            default-date=${DateTime.now()}
          ></cr-date-picker-dropdown>
        </cr-grid>
        <cr-textarea ${this._form.field('description')} placeholder="Beschreibung"></cr-textarea>
        <cr-text ${css({ fontStyle: 'italic' })}> Externe Links müssen das https:// beinhalten </cr-text>
        <cr-input ${this._form.field('urlToEvent')} placeholder="Event-Link"></cr-input>
        <cr-input ${this._form.field('urlToModeration')} placeholder="Moderations-Link"></cr-input>
      </cr-card>
    `;
  }

  protected override header() {
    return html`
      <cr-flex horizontal wrap end space="sm">
        <campus-delete-button
          @click=${async () => {
            if (!(await ChromiumDialog.confirm('Event wirklich löschen?'))) return;
            await this.event.value.remove();
            goTo(CampusRoutes.eventAdmin);
          }}
        >
        </campus-delete-button>
        <cr-button @click=${() => this._form.validate()} class="submit" flex> Speichern </cr-button>
      </cr-flex>
    `;
  }
}
